import { cn, formatNumber } from '@/lib/utils';
import {
    RefreshCw, Share2, Link2, MousePointerClick, UserPlus, Users,
    AlertTriangle, ArrowRight, TrendingUp, Repeat2,
} from 'lucide-react';
import {
    Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
    Tooltip, XAxis, YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    useProductViralCycle,
    SHARE_METHOD_LABELS,
    SHORT_URL_TYPE_LABELS,
    contentTypeLabel,
    type ProductViralCycleAnalytics,
    type ViralFunnelRate,
} from '@/hooks/useProductViralCycle';
import { pctPointDelta } from '@/hooks/useProductGrowthLoop';

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

function fmtPct(v: number | null): string {
    if (v == null) return 'n/d';
    return `${v.toLocaleString('es-ES', { maximumFractionDigits: 1 })}%`;
}

function DeltaBadge({ current, previous }: { current: number | null; previous: number | null }) {
    const delta = pctPointDelta(current, previous);
    if (delta == null) return null;
    const good = delta >= 0;
    return (
        <span className={cn('text-[10px] font-semibold', good ? 'text-emerald-400' : 'text-rose-400')}>
            {delta > 0 ? '+' : ''}{delta} pp
        </span>
    );
}

function LoopStage({
    label,
    value,
    icon,
    muted,
}: {
    label: string;
    value: number;
    icon: React.ReactNode;
    muted?: boolean;
}) {
    return (
        <div className={cn(
            'flex flex-col items-center min-w-[88px] px-2 py-3 rounded-lg border text-center',
            muted ? 'border-border/50 bg-muted/20 opacity-70' : 'border-border bg-card',
        )}>
            <span className="text-primary mb-1">{icon}</span>
            <p className="text-lg font-bold tabular-nums text-foreground">{formatNumber(value)}</p>
            <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{label}</p>
        </div>
    );
}

function ViralLoopFunnel({ data }: { data: ProductViralCycleAnalytics }) {
    const l = data.loop;
    const stageValues = [
        { label: 'Activos', value: l.active_users, icon: <Users size={14} /> },
        { label: 'Comparten', value: l.sharers, icon: <Share2 size={14} /> },
        { label: 'Acciones', value: l.viral_actions, icon: <Repeat2 size={14} /> },
        { label: 'URLs cortas', value: l.short_urls_created, icon: <Link2 size={14} /> },
        { label: 'Clicks', value: l.link_clicks, icon: <MousePointerClick size={14} /> },
        { label: 'Llegadas', value: l.viral_arrivals, icon: <TrendingUp size={14} />, muted: !data.measurement.attribution_ready },
        { label: 'Registros', value: l.attributed_signups, icon: <UserPlus size={14} /> },
    ];

    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3 overflow-x-auto">
            <div>
                <h3 className="text-sm font-semibold text-foreground">Embudo del ciclo viral</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5 max-w-3xl">
                    De usuario activo a registro atribuible. Clicks = acumulado en URLs creadas en el periodo.
                    Llegadas = first_open_context con enlace /c, /m, /u o /invite.
                </p>
            </div>
            <div className="flex items-center gap-1 min-w-max pb-1">
                {stageValues.map((s, i) => (
                    <div key={s.label} className="flex items-center gap-1">
                        <LoopStage label={s.label} value={s.value} icon={s.icon} muted={s.muted} />
                        {i < stageValues.length - 1 ? (
                            <ArrowRight size={14} className="text-muted-foreground shrink-0" />
                        ) : null}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                    <p className="text-muted-foreground">Penetración sharers</p>
                    <p className="font-bold text-foreground tabular-nums">{fmtPct(l.sharer_penetration_pct)}</p>
                    <DeltaBadge current={l.sharer_penetration_pct} previous={l.sharer_penetration_pct_previous} />
                </div>
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                    <p className="text-muted-foreground">Acciones / sharer</p>
                    <p className="font-bold text-foreground tabular-nums">{l.actions_per_sharer?.toFixed(1) ?? 'n/d'}</p>
                </div>
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                    <p className="text-muted-foreground">CTR shares→clicks</p>
                    <p className="font-bold text-foreground tabular-nums">{fmtPct(l.click_through_pct)}</p>
                    <DeltaBadge current={l.click_through_pct} previous={l.click_through_pct_previous} />
                </div>
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                    <p className="text-muted-foreground">K (periodo)</p>
                    <p className="font-bold text-foreground tabular-nums">{l.k_factor?.toFixed(3) ?? 'n/d'}</p>
                </div>
            </div>
        </div>
    );
}

function FunnelRatesTable({ rates }: { rates: ViralFunnelRate[] }) {
    if (rates.length === 0) return null;
    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Conversiones entre etapas</h3>
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[11px] text-muted-foreground border-b border-border">
                        <th className="text-left font-medium px-4 py-2">Paso</th>
                        <th className="text-right font-medium px-3 py-2">Tasa</th>
                        <th className="text-right font-medium px-4 py-2">Numerador / denominador</th>
                    </tr>
                </thead>
                <tbody>
                    {rates.map((r) => (
                        <tr key={r.stage} className="border-b border-border/60 last:border-0">
                            <td className="px-4 py-2.5 text-foreground">{r.label}</td>
                            <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-primary">
                                {fmtPct(r.rate_pct)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground text-[11px]">
                                {formatNumber(r.numerator)} / {formatNumber(r.denominator)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function WeeklyTrendChart({ data }: { data: ProductViralCycleAnalytics }) {
    const chartData = data.weekly_trend.map((p) => ({
        ...p,
        label: p.week_start
            ? format(parseISO(String(p.week_start).split('T')[0]), 'd MMM', { locale: es })
            : '',
    }));
    if (chartData.length === 0) return null;

    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">Tendencia semanal (shares y sharers)</h3>
            <p className="text-[11px] text-muted-foreground mb-3">Últimas ~10 semanas · analytics_events + short_urls</p>
            <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'hsl(215 20% 55%)' }} />
                        <YAxis tick={{ fontSize: 10, fill: 'hsl(215 20% 55%)' }} width={32} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="shares" name="Shares" stroke="hsl(199, 89%, 48%)" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sharers" name="Sharers" stroke="hsl(262, 83%, 58%)" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="clicks" name="Clicks (URLs nuevas)" stroke="hsl(172, 66%, 50%)" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function SharerDistributionChart({ data }: { data: ProductViralCycleAnalytics }) {
    const chartData = data.sharer_distribution.map((b) => ({ name: `${b.bucket} shares`, sharers: b.sharers }));
    if (chartData.length === 0) return null;

    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">Distribución de sharers</h3>
            <p className="text-[11px] text-muted-foreground mb-3">Shares por usuario que compartió en el periodo</p>
            <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(215 20% 55%)' }} />
                        <YAxis tick={{ fontSize: 10, fill: 'hsl(215 20% 55%)' }} width={28} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="sharers" name="Usuarios" fill="hsl(199, 89%, 48%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function ReferralQualityBlock({ data }: { data: ProductViralCycleAnalytics }) {
    const rq = data.referral_quality;
    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div>
                <h3 className="text-sm font-semibold text-foreground">Calidad de referidos (histórico)</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                    Invitados por código ({formatNumber(rq.invited_users)}) vs. orgánicos ({formatNumber(rq.organic_users)}).
                </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-border/60 p-3 space-y-2">
                    <p className="text-xs font-semibold text-foreground">Por invitación</p>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Activación onboarding</span>
                        <span className="font-bold tabular-nums">{fmtPct(rq.invited_activation_pct)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Con captura</span>
                        <span className="font-bold tabular-nums text-emerald-400">{fmtPct(rq.invited_capture_pct)}</span>
                    </div>
                </div>
                <div className="rounded-lg border border-border/60 p-3 space-y-2">
                    <p className="text-xs font-semibold text-foreground">Orgánico</p>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Activación onboarding</span>
                        <span className="font-bold tabular-nums">{fmtPct(rq.organic_activation_pct)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Con captura</span>
                        <span className="font-bold tabular-nums">{fmtPct(rq.organic_capture_pct)}</span>
                    </div>
                </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
                Códigos emitidos: {formatNumber(rq.invitation_codes_issued)} · Invitadores históricos: {formatNumber(rq.inviters_all_time)}.
            </p>
        </div>
    );
}

function BreakdownGrid({ data }: { data: ProductViralCycleAnalytics }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-semibold text-foreground mb-2">Contenido compartido</h3>
                <div className="space-y-1.5">
                    {data.content_types.map((row) => (
                        <div key={row.event_type} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{contentTypeLabel(row.event_type ?? '')}</span>
                            <span className="tabular-nums font-medium">
                                {formatNumber(row.count)}
                                <span className="text-[10px] text-muted-foreground"> ({formatNumber(row.users ?? 0)} usr)</span>
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-semibold text-foreground mb-2">URLs cortas por tipo</h3>
                <div className="space-y-1.5">
                    {data.short_url_types.map((row) => (
                        <div key={row.share_type} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{SHORT_URL_TYPE_LABELS[row.share_type ?? ''] ?? row.share_type}</span>
                            <span className="tabular-nums font-medium">{formatNumber(row.urls ?? 0)} urls · {formatNumber(row.clicks ?? 0)} clicks</span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-semibold text-foreground mb-2">Canal de share</h3>
                <div className="space-y-1.5">
                    {data.methods.map((row) => (
                        <div key={row.method} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{SHARE_METHOD_LABELS[row.method ?? ''] ?? row.method}</span>
                            <span className="tabular-nums font-medium">{formatNumber(row.count)}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-semibold text-foreground mb-2">Plataforma</h3>
                <div className="space-y-1.5">
                    {data.platforms.map((row) => (
                        <div key={row.platform} className="flex justify-between text-sm">
                            <span className="text-muted-foreground capitalize">{row.platform}</span>
                            <span className="tabular-nums font-medium">{formatNumber(row.count)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

interface ProductViralCyclePanelProps {
    days: number;
}

export function ProductViralCyclePanel({ days }: ProductViralCyclePanelProps) {
    const { data, isLoading, isError, error, refetch, isFetching } = useProductViralCycle(days);

    if (isLoading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-6 flex items-center gap-2">
                <RefreshCw size={16} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Analizando ciclo viral…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 space-y-2">
                <p className="text-sm font-semibold text-rose-400">No se pudo cargar el ciclo viral</p>
                <p className="text-xs text-muted-foreground">{(error as Error)?.message}</p>
                <button type="button" onClick={() => refetch()} className="text-xs text-primary hover:underline">Reintentar</button>
            </div>
        );
    }

    if (!data) return null;

    const diag = data.diagnosis;
    const ms = data.measurement;
    const bottleneckTone = diag.bottleneck_key === 'healthy' ? 'border-emerald-500/30 bg-emerald-500/5'
        : diag.bottleneck_key === 'attribution' ? 'border-amber-500/30 bg-amber-500/5'
            : 'border-primary/30 bg-primary/5';

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-base font-bold text-foreground">Ciclo viral de la App</h2>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-3xl">
                        Seguimiento del loop usuario → share → enlace → click → instalación/registro.
                        Periodo: {days} días. {data.comparison_label}.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 shrink-0"
                    title="Actualizar ciclo viral"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className={cn('rounded-xl border p-4 flex gap-3', bottleneckTone)}>
                <AlertTriangle size={18} className="text-primary shrink-0 mt-0.5" />
                <div className="space-y-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                        Cuello de botella: {diag.bottleneck_label}
                    </p>
                    <p className="text-xs text-muted-foreground">{diag.headline}</p>
                    {!ms.attribution_ready ? (
                        <p className="text-[11px] text-amber-400/90 mt-1">
                            Medición incompleta: first_open_context tiene {formatNumber(ms.first_open_total)} eventos históricos.
                            Sin esto no cerramos el embudo share → registro por enlace RRSS.
                        </p>
                    ) : null}
                </div>
            </div>

            <ViralLoopFunnel data={data} />
            <FunnelRatesTable rates={data.funnel_rates} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <WeeklyTrendChart data={data} />
                <SharerDistributionChart data={data} />
            </div>

            <BreakdownGrid data={data} />
            <ReferralQualityBlock data={data} />

            <p className="text-[11px] text-muted-foreground">
                Histórico URLs: {formatNumber(ms.short_urls_all_time)} enlaces · {formatNumber(ms.short_url_clicks_all_time)} clicks totales.
                Los clicks en periodo son acumulados sobre URLs creadas en el periodo (no hay log de click por día).
            </p>
        </div>
    );
}
